<template>
    <div class="share-page">
        <header class="share-header">
            <div class="share-back">
                <button type="button" class="back-btn" @click="goHome">
                    <ChatIcon name="arrow-left" :size="14" />
                    <span>В чат AM Analytics</span>
                </button>
            </div>

            <div v-if="loading" class="share-skeleton">
                <div class="skeleton-line skeleton-title" />
                <div class="skeleton-line skeleton-subtitle" />
            </div>

            <div v-else-if="error" class="share-error">
                <h1>Ссылка недоступна</h1>
                <p>
                    Возможно, владелец отозвал её или срок действия истёк.
                    Запросите свежую ссылку у того, кто поделился.
                </p>
                <button type="button" class="primary-btn" @click="goHome">
                    Открыть свой чат
                </button>
            </div>

            <div v-else class="share-meta">
                <span class="badge">
                    <ChatIcon name="link" :size="11" />
                    Поделено
                </span>
                <h1 class="share-title">{{ share.thread?.title || 'Фрагмент диалога' }}</h1>
                <p class="share-sub">
                    {{ messages.length }} {{ messageWord }} в этом срезе ·
                    режим — <strong>{{ modeLabel }}</strong>
                </p>

                <div class="share-actions">
                    <button
                        v-if="canFork && isAuthenticated"
                        type="button"
                        class="primary-btn"
                        :disabled="forking"
                        @click="onFork"
                    >
                        <ChatIcon name="copy" :size="13" />
                        {{ forking ? 'Открываю…' : 'Продолжить как свою ветку' }}
                    </button>
                    <button
                        v-else-if="canFork && !isAuthenticated"
                        type="button"
                        class="primary-btn"
                        @click="goToLogin"
                    >
                        Войти, чтобы продолжить
                    </button>
                </div>
            </div>
        </header>

        <main v-if="!loading && !error" class="share-body">
            <div v-if="messages.length === 0" class="empty-state">
                В этом срезе нет сообщений.
            </div>
            <div v-else class="messages-list">
                <ChatMessage
                    v-for="m in messages"
                    :key="m.id"
                    :message="m"
                    :user-name="m.role === 'user' ? 'Поделено' : ''"
                    :user-initials="m.role === 'user' ? 'ПД' : 'AM'"
                />
            </div>
        </main>
    </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore as useVuexStore } from 'vuex'
import ChatIcon from '@/components/chat/icons/ChatIcon.vue'
import ChatMessage from '@/components/chat/conversation/ChatMessage.vue'
import { getShareByToken, forkShare } from '@/utils/chatApi'
import { rebuildMessageFromServer } from '@/components/chat/state/useChatStore'

const route = useRoute()
const router = useRouter()
const vuexStore = useVuexStore()

const loading = ref(true)
const error = ref(false)
const forking = ref(false)
const share = ref({})
const messages = ref([])

const isAuthenticated = computed(() => {
    const info = vuexStore.getters['main/userInfo']
    return info && Object.keys(info).length > 0
})

const canFork = computed(() => share.value?.mode === 'forkable')

const modeLabel = computed(() => (canFork.value ? 'разрешено продолжать' : 'только просмотр'))

function plural(n, forms) {
    const abs = Math.abs(n) % 100
    if (abs >= 11 && abs <= 14) return forms[2]
    const last = abs % 10
    if (last === 1) return forms[0]
    if (last >= 2 && last <= 4) return forms[1]
    return forms[2]
}

const messageWord = computed(() => plural(messages.value.length, ['сообщение', 'сообщения', 'сообщений']))

async function load() {
    const token = route.params.token
    if (!token) {
        error.value = true
        loading.value = false
        return
    }
    loading.value = true
    error.value = false
    try {
        const data = await getShareByToken(token)
        share.value = data
        messages.value = (data.messages || []).map(rebuildMessageFromServer)
    } catch (e) {
        error.value = true
        share.value = {}
        messages.value = []
    } finally {
        loading.value = false
    }
}

async function onFork() {
    const token = route.params.token
    if (!token || forking.value) return
    forking.value = true
    try {
        const threadId = await forkShare(token)
        if (threadId) {
            router.push({ name: 'Chat', query: { thread: threadId } })
        }
    } catch (e) {
        forking.value = false
    }
}

function goHome() {
    router.push({ name: 'Chat' })
}

function goToLogin() {
    router.push({ name: 'Login', query: { redirect: route.fullPath } })
}

onMounted(load)
watch(() => route.params.token, load)
</script>

<style scoped>
.share-page { min-height: 100vh; background: white; padding: 0 0 64px; }

.share-header {
    max-width: 880px;
    margin: 0 auto;
    padding: 24px 24px 12px;
}

.share-back { margin-bottom: 16px; }

.back-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: transparent;
    border: 0;
    font-family: inherit;
    font-size: 13px;
    color: #5f5f5f;
    cursor: pointer;
    padding: 4px 6px;
    margin-left: -6px;
    border-radius: 6px;
}

.back-btn:hover { color: #23282C; background: #f0f0f0; }

.share-skeleton { padding: 24px 0; }

.skeleton-line { background: #f0f0f0; border-radius: 6px; margin-bottom: 12px; animation: skel 1.2s ease-in-out infinite; }
.skeleton-title { height: 28px; width: 70%; }
.skeleton-subtitle { height: 16px; width: 40%; }

@keyframes skel { 0%, 100% { opacity: 1; } 50% { opacity: 0.6; } }

.share-error {
    padding: 60px 0;
    text-align: center;
    color: #5f5f5f;
}
.share-error h1 { font-size: 20px; margin: 0 0 8px; color: #23282C; }
.share-error p { margin: 0 auto 24px; font-size: 14px; max-width: 480px; line-height: 1.55; }

.badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: #FFF4EE;
    color: #C0050F;
    padding: 3px 8px;
    border-radius: 4px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    font-size: 10.5px;
}

.share-title {
    font-size: 26px;
    font-weight: 700;
    color: #23282C;
    margin: 10px 0 6px;
    line-height: 1.25;
}

.share-sub {
    margin: 0 0 18px;
    font-size: 13px;
    color: #5f5f5f;
}

.share-sub strong { color: #23282C; }

.share-actions {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    margin-bottom: 12px;
}

.primary-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #E30613;
    color: white;
    border: 0;
    padding: 9px 14px;
    font-size: 13px;
    font-weight: 600;
    border-radius: 6px;
    cursor: pointer;
    font-family: inherit;
}

.primary-btn:hover { background: #C0050F; }
.primary-btn:disabled { opacity: 0.6; cursor: progress; }

.share-body {
    max-width: 880px;
    margin: 0 auto;
    padding: 12px 24px;
    border-top: 1px solid #e7e7e7;
}

.empty-state { padding: 40px 0; text-align: center; color: #888888; font-size: 13px; }

.messages-list { display: flex; flex-direction: column; gap: 12px; }

@media (max-width: 720px) {
    .share-header { padding: 16px 16px 8px; }
    .share-body { padding: 12px 16px; }
    .share-title { font-size: 22px; }
}
</style>
